<template>
  <!-- The storefront is switched off (or this host is not a store). Render ONLY
       this: every other surface below would fire API calls that 503, and the
       cart/checkout must not be reachable on a closed shop. -->
  <StoreUnavailable v-if="bootstrap.unavailable" :reason="bootstrap.unavailable" />
  <template v-else>
  <!-- Experience: page-load preloader (repeat visits know the toggle from cached
       bootstrap; dismissed on first frame after load so it never blocks LCP). -->
  <div v-if="exp.preloader && !preloaderDone" class="dk-preloader"><span class="dk-preloader__spin" /></div>
  <!-- Experience: scroll-progress bar. -->
  <div v-if="!checkoutFrame && exp.scroll_progress" class="dk-scrollbar">
    <span :style="{ transform: `scaleX(${scrollPct})` }" />
  </div>
  <!-- Navigation-hold indicator. lib/navhold keeps the current page on screen
       until the next one's data is ready; this is the only signal the shopper gets
       that their tap landed. The 120ms animation-delay means a hold that resolves
       from cache (prefetched, or a revisit) shows literally nothing. -->
  <div v-if="navPending" class="dk-navbar" aria-hidden="true" />

  <div
    v-if="!checkoutFrame && announceEnabled && messages.length && !announceClosed"
    class="dk-announce"
    :class="[announceSticky ? 'dk-announce--sticky' : null, `dk-announce--${announceAlign}`]"
  >
    <component
      :is="announceItems[current]?.link ? 'a' : 'span'"
      class="dk-announce__msg"
      :href="announceItems[current]?.link || undefined"
      :style="blockLayoutStyle(announceItems[current]?.settings)"
      :class="blockLayoutClass(announceItems[current]?.settings)"
      :data-dk-block="announceItems[current]?.id || undefined"
    >
      <Transition name="dk-announce-fade" mode="out-in">
        <span :key="current">{{ messages[current] }}</span>
      </Transition>
    </component>
    <button
      v-if="announceDismissible"
      type="button"
      class="dk-announce__x"
      aria-label="Close announcement"
      @click="closeAnnounce"
    >
      ✕
    </button>
  </div>
  <SiteHeader v-if="!checkoutFrame" />
  <!-- Checkout funnel gets its own frame (minimal header + policy footer); every
       other route renders inside the storefront chrome. -->
  <component :is="checkoutFrame ? CheckoutLayout : 'main'" :class="checkoutFrame ? undefined : 'dk-main'">
    <RouterView v-slot="{ Component, route: r }">
      <!-- No <Transition> on purpose — see the .dk-page note in styles.css. Vue's
           leave path always costs 2 animation frames and mode="out-in" did not
           create the incoming page until afterLeave, so vue-router's
           scrollBehavior (one microtask later) was GUARANTEED to scroll the
           outgoing page and to leave <main> empty in between. A keyed swap with no
           Transition is atomic within a single patch. -->
      <div :key="r.path" class="dk-page-wrap">
        <component :is="Component" />
      </div>
    </RouterView>
  </component>
  <!-- Anything in `layout.footer` that ISN'T the footer itself renders above it,
       on every page — which is what makes `footer-promotions` a footer-group
       section rather than one a seller has to repeat on each template. -->
  <SectionList v-if="!checkoutFrame && footerOpen && aboveFooterSections.length" :sections="aboveFooterSections" />
  <SiteFooter v-if="!checkoutFrame && footerOpen" />
  <CartDrawer v-if="!checkoutFrame" />
  <PromoPopup v-if="!checkoutFrame" />
  <!-- Age gate covers the checkout frame too: if a store needs confirmation to be
       browsed, it needs it to be bought from. -->
  <AgeGate />
  <CookieConsent v-if="!checkoutFrame" />
  <BackToTop v-if="!checkoutFrame && exp.back_to_top" />

  <!-- Experience: sticky bottom call-to-action bar. -->
  <component
    :is="exp.sticky_bar_link ? 'a' : 'div'"
    v-if="!checkoutFrame && exp.sticky_bar && stickyText"
    class="dk-stickybar"
    :href="exp.sticky_bar_link || undefined"
  >
    <span>{{ stickyText }}</span>
    <span v-if="exp.sticky_bar_button" class="dk-btn dk-stickybar__btn">{{ exp.sticky_bar_button }}</span>
  </component>
  </template>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useRoute } from "vue-router"
import type { AnnouncementSettings, ExperienceSettings } from "@dk/theme"
// The two block-layout resolvers are the ONE static value import of @dk/theme in
// this entry-resident file. They live in `block-design.ts` and pull nothing else
// in — measured against the 70 kB budget below; see applyTheme() for why the CSS
// generator and the section barrel must stay dynamic.
import { blockLayoutClass, blockLayoutStyle } from "@dk/theme"
// AgeGate stays EAGER on purpose: an age gate that mounts a frame late has
// already shown the store it was meant to withhold. The other two are off by
// default and cosmetic/non-blocking (the pixel gate itself lives in
// lib/tracking, not in the banner), so they load on demand.
import AgeGate from "./components/AgeGate.vue"
import CartDrawer from "./components/CartDrawer.vue"
// EAGER, and it must be: this is what renders when the tenant could not be
// resolved, so a lazy chunk would need the very network round trip that just
// failed. It is a few hundred bytes of markup + literal CSS.
import StoreUnavailable from "./components/StoreUnavailable.vue"

const BackToTop = defineAsyncComponent(() => import("./components/BackToTop.vue"))
const CookieConsent = defineAsyncComponent(() => import("./components/CookieConsent.vue"))
// LAZY for the same reason as those two, and it is the strongest case of the
// three: the popup is off by default AND, when a seller does switch it on, it is
// deliberately withheld for `delay_seconds` (0–30, default 4). A component that
// cannot paint for seconds has no business in the render-blocking global
// stylesheet — its scoped rules were ~0.7 kB of `index.css`, on a sheet whose CI
// budget has ~0 headroom. A chunk fetch is far inside its own delay.
const PromoPopup = defineAsyncComponent(() => import("./components/PromoPopup.vue"))
import CheckoutLayout from "./components/CheckoutLayout.vue"
import SiteFooter from "./components/SiteFooter.vue"
import SiteHeader from "./components/SiteHeader.vue"
import { linkUrl } from "./sections/helpers"

// LAZY, and it has to be: SectionList reaches the ~75-entry section registry,
// and importing it eagerly from the app shell pulled that whole module graph's
// preload metadata into the ENTRY chunk — 66.7 -> 72.4 kB gz, straight through
// the 70 kB budget. Every page that renders sections is a lazy route, so this
// component was never in the entry before and must not be now. Nothing below
// the fold blocks first paint anyway.
const SectionList = defineAsyncComponent(() => import("./components/SectionList.vue"))
import { setBaseHead } from "./lib/head"
import { readConsent } from "./lib/consent"
import { navPending } from "./lib/navhold"
import { isPreview } from "./lib/previewFlag"
import { keepThemeLast } from "./lib/themeorder"
import { openTrackingGate } from "./lib/tracking"
import { ensureVisitor } from "./lib/visitor"
import { useAccountStore } from "./stores/account"
import { useBootstrapStore } from "./stores/bootstrap"
import { useCartStore } from "./stores/cart"

const bootstrap = useBootstrapStore()
const cart = useCartStore()
const account = useAccountStore()
const route = useRoute()

// Routes flagged `meta.layout === "checkout"` (checkout · upsell · thank-you) run
// in the chrome-free funnel frame — no nav, footer, cart drawer, popup or sticky
// bar competing with the buyer finishing the order.
const checkoutFrame = computed(() => route.meta.layout === "checkout")

// The footer waits for the first page to fill the screen or go quiet
// (lib/footergate.ts), so it is inserted below the page instead of being pushed
// off the first screen by it. One-way: once open, it stays for the visit.
// START_LOCATION is the only route with nothing matched; the catch-all 404 matches
// every other URL, so this is "the first page has committed".
const pageCommitted = computed(() => route.matched.length > 0)
const footerOpen = ref(false)
let stopFooterGate: (() => void) | undefined
watch(
  pageCommitted,
  (committed) => {
    if (!committed || footerOpen.value || stopFooterGate) return
    void nextTick(() => {
      const page = document.querySelector<HTMLElement>(".dk-main > .dk-page-wrap")
      if (!page) {
        footerOpen.value = true
        return
      }
      // Lazy: it runs once a visit, and the footer is held back until it answers
      // anyway. Kept out of the entry chunk's budget for that reason. A chunk that
      // fails to load opens the footer, the old behaviour, rather than hiding it.
      import("./lib/footergate")
        .then((m) => (stopFooterGate = m.watchFooterGate(page, () => (footerOpen.value = true))))
        .catch(() => (footerOpen.value = true))
    })
  },
  { immediate: true },
)
onBeforeUnmount(() => stopFooterGate?.())

// `layout.footer` holds the footer AND anything a seller places above it
// (`footer-promotions`). The footer itself is rendered by SiteFooter, so it is
// excluded here or it would render twice.
const aboveFooterSections = computed(() =>
  (bootstrap.themeDoc?.layout?.footer ?? []).filter(s => s.type !== "footer"),
)

// ---- announcement bar ------------------------------------------------------
// Two sources, same precedence as the header and footer: the `announcement-bar`
// SECTION when the theme has one, else the `ThemeSettings.announcement` group.
// The group holds every message in one newline-separated string with a single
// shared link; the section gives each message its own block and its own link,
// which is what Impulse does.
const announcement = computed<AnnouncementSettings>(() => bootstrap.themeSettings.announcement ?? {})
const announceSection = computed(() =>
  (bootstrap.themeDoc?.layout?.header ?? []).find(s => s.type === "announcement-bar" && s.visible !== false),
)
const announceBlocks = computed(() =>
  (announceSection.value?.blocks ?? []).filter(b => b.visible !== false && String(b.settings.text ?? "").trim()),
)
const announceSettings = computed<Record<string, unknown>>(() => announceSection.value?.settings ?? {})

/** Messages, as {text, link} — a section block's own link wins; the group's
 *  single link applies to all of its messages, which is all it can express. */
const announceItems = computed(() => {
  if (announceSection.value) {
    // `settings` rides along so the per-block layout overrides can be resolved in
    // the template; the GROUP fallback below has no per-message settings to give,
    // so it hands back an empty bag, which both resolvers treat as "untouched".
    return announceBlocks.value.map(b => ({
      text: String(b.settings.text).trim(),
      link: linkUrl(b.settings.link) || "",
      settings: b.settings,
      id: b.id,
    }))
  }
  return (announcement.value.messages ?? announcement.value.text ?? "")
    .split("\n")
    .map(m => m.trim())
    .filter(Boolean)
    .map(text => ({ text, link: announcement.value.link ?? "", settings: {} as Record<string, unknown>, id: "" }))
})
const messages = computed(() => announceItems.value.map(i => i.text))
const announceEnabled = computed(() =>
  announceSection.value ? true : announcement.value.enabled === true,
)
const announceDismissible = computed(() =>
  announceSection.value ? announceSettings.value.dismissible === true : announcement.value.dismissible === true,
)
const announceSticky = computed(() => announceSettings.value.sticky === true)
const announceAlign = computed(() => String(announceSettings.value.align ?? "center"))
const announceRotate = computed(() =>
  Number(announceSection.value ? announceSettings.value.rotate_seconds : announcement.value.rotate_seconds),
)
const current = ref(0)

// Announcement dismissal, remembered per visitor (host-scoped like the other
// storefront keys). Keyed by the MESSAGE TEXT so that publishing a new
// announcement shows again to someone who closed the previous one.
const ANNOUNCE_KEY = `dk:announce:${location.host}`
const announceClosed = ref(false)
watch(
  messages,
  list => {
    if (!list.length) return
    try {
      announceClosed.value = localStorage.getItem(ANNOUNCE_KEY) === list.join("|")
    } catch {
      announceClosed.value = false
    }
  },
  { immediate: true },
)
function closeAnnounce() {
  announceClosed.value = true
  try {
    localStorage.setItem(ANNOUNCE_KEY, messages.value.join("|"))
  } catch {
    /* storage blocked → it reappears next visit, which is the harmless direction */
  }
}
let annTimer: ReturnType<typeof setInterval> | undefined
// The slideshow's contract (Slideshow.vue): a visitor who asked the OS for reduced
// motion gets a still bar, on its first message. Also what makes two theme-lab
// captures of one store agree — the message on screen used to depend on how long
// the page took to load.
const annStill = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches
watch(
  [messages, announceRotate],
  () => {
    if (annTimer) clearInterval(annTimer)
    current.value = 0
    if (messages.value.length > 1 && !annStill) {
      const sec = Math.max(3, Number.isFinite(announceRotate.value) ? announceRotate.value : 5)
      annTimer = setInterval(() => {
        current.value = (current.value + 1) % messages.value.length
      }, sec * 1000)
    }
  },
  { immediate: true },
)
onBeforeUnmount(() => annTimer && clearInterval(annTimer))

// ---- Experience toggles (preloader / scroll-progress / sticky bar) ---------
const exp = computed<ExperienceSettings>(() => bootstrap.themeSettings.experience ?? {})
const stickyText = computed(() => String(exp.value.sticky_bar_text ?? "").trim())

// Scroll-progress bar: rAF-throttled, only wired while the toggle is on.
const scrollPct = ref(0)
let rafPending = false
function onScroll() {
  if (rafPending) return
  rafPending = true
  requestAnimationFrame(() => {
    rafPending = false
    const h = document.documentElement
    const max = h.scrollHeight - h.clientHeight
    scrollPct.value = max > 0 ? Math.min(1, h.scrollTop / max) : 0
  })
}
watch(
  () => exp.value.scroll_progress,
  (on) => {
    window.removeEventListener("scroll", onScroll)
    if (on) {
      window.addEventListener("scroll", onScroll, { passive: true })
      onScroll()
    }
  },
  { immediate: true },
)

// Preloader: hidden as soon as the app is ready (bootstrap resolved + first
// frame). A safety timeout guarantees it never sticks if load() rejects.
const preloaderDone = ref(false)
function dismissPreloader() {
  requestAnimationFrame(() => (preloaderDone.value = true))
}
onBeforeUnmount(() => window.removeEventListener("scroll", onScroll))

// Started at boot rather than when bootstrap resolves: #dk-theme-vars must exist
// before the first lazy section's stylesheet lands, and the chunk is fetched on
// every load anyway. keepThemeLast() then keeps the node behind every later one.
const cssVarsModule = import("@dk/theme/css-vars")
keepThemeLast()

/** Theme CSS vars go into a dedicated style node; the builder preview patches
 *  this same node (and favicon) live when theme settings change. */
async function applyTheme() {
  // Loaded on demand, NOT statically: this is the only entry-resident runtime
  // import of @dk/theme, so a static import puts the whole CSS generator (and
  // every preset lookup table it will grow) inside the 70 kB initial-JS budget.
  // Deferring it is safe because the render-blocking, Go-served
  // /api/storefront/theme.css already painted the identical bytes on frame 1 —
  // this node exists only to win the cascade for the builder preview and to
  // let a previewed theme UN-declare tokens.
  // Deep import, NOT the "@dk/theme" barrel: the barrel builds `sectionDefs` from
  // all 65 section modules, so a dynamic import of it pulls every section's labels
  // and FieldDefs into this chunk (~9.6 kB gz) — builder-only schema, fetched on
  // every storefront page load. `pnpm size` does not catch it: it measures
  // entry-*.js only, and this is a separate async chunk.
  const { themeToStylesheet } = await cssVarsModule
  let styleEl = document.getElementById("dk-theme-vars")
  if (!styleEl) {
    styleEl = document.createElement("style")
    styleEl.id = "dk-theme-vars"
  }
  styleEl.textContent = themeToStylesheet(bootstrap.themeSettings)
  // Re-append unconditionally so this node is always LAST in <head> — after the
  // per-tenant /theme.css link and the bundle CSS — and therefore wins the cascade
  // (button-fill and card-hover are now vars in this string, no root class needed).
  document.head.appendChild(styleEl)

  // In preview (builder / marketplace), drop the published server stylesheet so the
  // previewed theme can UN-declare tokens it removed; a later <style> can only
  // override, never un-declare. Remove after the write above → no unstyled frame.
  if (isPreview()) document.getElementById("dk-theme-css")?.remove()

  const favicon = bootstrap.themeSettings.favicon
  if (favicon) {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
    if (!link) {
      link = document.createElement("link")
      link.rel = "icon"
      document.head.appendChild(link)
    }
    link.href = favicon
  }
}

/** Site-wide head defaults (title suffix, share image, locale). Pages override
 *  per-route via useHead(); this is the fallback + OG site_name. */
function applyBaseHead() {
  if (isPreview()) return // builder iframe: don't touch the parent-visible head
  const store = bootstrap.store
  setBaseHead({
    siteName: bootstrap.storeName || "Store",
    image: bootstrap.themeSettings.logo?.src,
    locale: store?.locale,
    // Whether this store asks to stay out of search. The bot prerender emits the
    // same meta for a crawler that does not run JS; this is the half a crawler
    // that DOES run JS reads.
    noindex: bootstrap.storeSettings.seo?.noindex === true,
  })
}

/** Inject seller custom HTML (head / end-of-body). Live storefront only — never
 *  in the builder preview iframe — and once per snippet. Scripts parsed from
 *  innerHTML don't run, so <script> nodes are recreated to execute. */
function injectSnippet(html: string | undefined, target: HTMLElement, marker: string) {
  if (!html?.trim() || isPreview()) return
  if (document.querySelector(`[data-dk-snippet="${marker}"]`)) return
  const holder = document.createElement("div")
  holder.setAttribute("data-dk-snippet", marker)
  holder.style.display = "none"
  const tpl = document.createElement("template")
  tpl.innerHTML = html
  for (const node of Array.from(tpl.content.childNodes)) {
    if (node.nodeName === "SCRIPT") {
      const orig = node as HTMLScriptElement
      const s = document.createElement("script")
      for (const attr of Array.from(orig.attributes)) s.setAttribute(attr.name, attr.value)
      s.text = orig.text
      holder.appendChild(s)
    } else {
      holder.appendChild(node.cloneNode(true))
    }
  }
  target.appendChild(holder)
}

onMounted(async () => {
  // Guarantee the preloader clears even if bootstrap.load() rejects.
  setTimeout(() => (preloaderDone.value = true), 4000)
  await bootstrap.load()
  // The builder channel is fetched only by a seller with the builder open. It
  // reaches into `@dk/theme`'s patch helpers and the whole postMessage protocol,
  // none of which a shopper has any use for — so it stays out of the entry chunk.
  if (isPreview()) await import("./lib/preview").then((m) => m.initPreview())
  await applyTheme()
  applyBaseHead()
  dismissPreloader()
  const scripts = (bootstrap.storeSettings.scripts?.custom ?? {}) as { head?: string; body?: string }
  injectSnippet(scripts.head, document.head, "head")
  injectSnippet(scripts.body, document.body, "body")
  void cart.load()
  void account.restore() // re-mint access token from persisted refresh token

  // Marketing pixels are held until now (see lib/tracking's gate). Release them
  // only if this store asks for no consent, or the visitor already accepted —
  // "denied" and "not yet answered" both keep every vendor script unloaded.
  const consent = bootstrap.themeSettings.consent ?? {}
  if (consent.enabled !== true || readConsent() === "granted") {
    openTrackingGate()
    // The visitor profile is a persistent identifier, so it lives on the same
    // side of the cookie banner as the pixels. It is what ties this browser's
    // cart to a contactable person — without it, recovery can only reach people
    // who already typed an address into checkout.
    void ensureVisitor()
  }
})

// The sticky bottom bar is `position: fixed`, so anything else pinned to the
// bottom (the back-to-top button) has to be told how tall it is.
watch(
  () => Boolean(exp.value.sticky_bar && stickyText.value),
  on => document.documentElement.style.setProperty("--dk-bottom-inset", on ? "3.2rem" : "0px"),
  { immediate: true },
)

// Live preview: re-apply CSS vars + favicon whenever theme settings change.
watch(() => bootstrap.themeSettings, applyTheme, { deep: true })
// Refresh base head once the store payload arrives (name/logo/locale).
watch(() => bootstrap.store, applyBaseHead)
</script>
